<template>
  <div class="user-management">
    <el-card shadow="never" class="main-card animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="filter-group">
            <el-input
              v-model="searchUserId"
              placeholder="用户ID"
              clearable
              class="filter-input-sm"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            />
            <el-input
              v-model="searchPhone"
              placeholder="手机号"
              clearable
              class="filter-input-sm"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            />
            <el-select
              v-model="searchStatus"
              placeholder="用户状态"
              clearable
              class="filter-select-sm"
              @change="handleSearch"
            >
              <el-option label="未封禁" :value="0" />
              <el-option label="已封禁" :value="1" />
            </el-select>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="refreshList">重置</el-button>
          </div>
          <div class="earned-summary">
            <i class="el-icon-coin"></i>
            <span class="earned-label">累计总收益</span>
            <span class="earned-value">{{ totalEarned }}</span>
            <span class="earned-unit">金币</span>
          </div>
        </div>
      </div>

      <div v-if="error" class="error-msg">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        :description="(searchUserId || searchPhone) ? '没有符合条件的用户' : '暂无用户数据'"
      />

      <div v-else class="table-wrapper">
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="data-table"
          row-key="id"
          :header-cell-style="{ background: '#fafafa', color: '#303133', fontWeight: 600 }"
        >
          <el-table-column prop="id" label="ID" width="72" align="center" />
          <el-table-column prop="openid" label="手机号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="inviteCode" label="邀请码" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain">{{ scope.row.inviteCode || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="parentId" label="上级ID" width="80" align="center" />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small" effect="plain">封禁</el-tag>
              <el-tag v-else-if="scope.row.status === 2" type="info" size="small" effect="plain">已注销</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="deviceRisk" label="设备风险" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.deviceRisk === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.deviceRisk === 1" type="danger" size="small" effect="plain">风险</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="位置" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ formatUserLocation(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="注册时间" width="160" align="center" />
          <el-table-column label="操作" width="150" align="center" fixed="right">
            <template slot-scope="scope">
              <div v-if="scope.row.status !== 2" class="user-ops">
                <el-button type="text" size="small" @click="showDetail(scope.row)">详情</el-button>
                <el-dropdown trigger="click" @command="cmd => handleMoreCommand(cmd, scope.row)">
                  <el-button type="text" size="small">
                    更多<i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="level">团长等级</el-dropdown-item>
                    <el-dropdown-item v-if="canEdit" command="toggle" divided>
                      {{ scope.row.status === 0 ? '禁用账号' : '启用账号' }}
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="canEdit && scope.row.status === 0"
                      command="delete"
                    >删除用户</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <div v-loading="detailLoading" element-loading-text="加载中..." class="detail-content">
        <div class="detail-header">
          <el-avatar v-if="detailUser.avatarUrl" :src="detailUser.avatarUrl" :size="56" shape="circle" />
          <div class="detail-name">{{ detailUser.nickname || '-' }}</div>
          <div class="detail-id">ID: {{ detailUser.id }}</div>
          <div class="detail-location">{{ formatUserLocation(detailUser) }}</div>
          <div v-if="detailUser.lastIp" class="detail-ip">IP: {{ detailUser.lastIp }}</div>
        </div>
        <div class="detail-cards">
          <div class="detail-card coin-current">
            <div class="detail-card-label">当前余额</div>
            <div class="detail-card-value">{{ detailUser.coinBalance || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-total">
            <div class="detail-card-label">累计余额</div>
            <div class="detail-card-value">{{ detailUser.totalEarnedCoins || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-invite">
            <div class="detail-card-label">奖励余额</div>
            <div class="detail-card-value">{{ detailUser.inviteRewardBalance || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-invite-total">
            <div class="detail-card-label">奖励累计</div>
            <div class="detail-card-value">{{ detailUser.inviteRewardTotal || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
        </div>
        <div v-if="detailUser.status === 1 && detailUser.banDes" class="detail-ban-reason">
          <div class="ban-reason-label">封禁原因</div>
          <div class="ban-reason-text">{{ detailUser.banDes }}</div>
        </div>
        <div v-if="detailUser.deviceRisk !== null && detailUser.deviceRisk !== undefined" class="detail-risk-section">
          <div class="risk-section-title">
            <i class="el-icon-warning-outline"></i>
            设备安全检测
            <el-tag :type="detailUser.deviceRisk === 1 ? 'danger' : 'success'" size="mini" class="risk-tag">
              {{ detailUser.deviceRisk === 1 ? '风险设备' : '正常设备' }}
            </el-tag>
          </div>
          <div v-if="parsedRiskDetail && parsedRiskDetail.length > 0" class="risk-items">
            <div
              v-for="(item, index) in parsedRiskDetail"
              :key="index"
              class="risk-item"
            >
              <div class="risk-item-header">
                <span class="risk-item-label">{{ item.label }}</span>
                <el-tag
                  :type="item.isRisk ? 'danger' : 'success'"
                  size="mini"
                  effect="plain"
                >
                  {{ item.isRisk ? '命中' : '正常' }}
                </el-tag>
              </div>
              <el-tooltip v-if="item.desc" :content="item.desc" placement="top-start" effect="dark">
                <span class="risk-item-desc">{{ item.desc }}</span>
              </el-tooltip>
            </div>
          </div>
          <div v-if="detailUser.deviceRiskDetail" class="risk-raw-json">
            <div class="risk-raw-title" @click="showRawJson = !showRawJson">
              <i :class="showRawJson ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              原始风险详情 JSON
            </div>
            <pre v-if="showRawJson" class="risk-json-content">{{ formattedRiskJson }}</pre>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="团长等级"
      :visible.sync="levelDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-table
        v-loading="levelLoading"
        :data="[levelUser]"
        border
        size="small"
        style="width: 100%"
      >
        <el-table-column label="团长等级" align="center" min-width="90">
          <template slot-scope="scope">
            {{ scope.row.levelCode == null ? 0 : scope.row.levelCode }}
          </template>
        </el-table-column>
        <el-table-column label="直推数" align="center" min-width="80">
          <template slot-scope="scope">
            {{ scope.row.directCount == null ? '-' : scope.row.directCount }}
          </template>
        </el-table-column>
        <el-table-column label="团队提现(元)" align="center" min-width="110">
          <template slot-scope="scope">
            {{ scope.row.teamWithdrawAmount == null ? '-' : scope.row.teamWithdrawAmount }}
          </template>
        </el-table-column>
      </el-table>
      <div class="level-tip">直推数仅统计状态为「正常」的下级（不含封禁/注销）</div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="levelDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="封禁用户"
      :visible.sync="banDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="banFormRef" :model="banForm" :rules="banRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="banUser ? banUser.nickname || banUser.id : ''" disabled />
        </el-form-item>
        <el-form-item label="封禁描述" prop="banDes">
          <el-input
            v-model.trim="banForm.banDes"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="banDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleBanSubmit">确认封禁</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put, del } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchUserId: '',
      searchPhone: '',
      searchStatus: '',
      totalEarned: 0,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      detailLoading: false,
      detailUser: {},
      levelDialogVisible: false,
      levelLoading: false,
      levelUser: {},
      banDialogVisible: false,
      banUser: null,
      banForm: {
        status: 1,
        banDes: ''
      },
      banRules: {
        banDes: [{ required: true, message: '请输入封禁描述', trigger: 'blur' }]
      },
      showRawJson: false,
      riskTagDict: {}
    }
  },
  computed: {
    // 所有后台账号均可操作
    canEdit() {
      const role = parseInt(localStorage.getItem('role'), 10)
      return role === 0 || role === 1
    },
    // 解析设备风险详情 JSON，返回可读的风险项列表
    // 风险标签中文释义来自后台 device_risk_tags
    parsedRiskDetail() {
      if (!this.detailUser.deviceRiskDetail) return []
      let raw = this.detailUser.deviceRiskDetail
      try {
        if (typeof raw === 'string') {
          raw = JSON.parse(raw)
        }
      } catch {
        // 解析失败，当作纯字符串处理
        raw = { tags: raw }
      }

      // 提取标签列表：支持 { tags: 'a,b' }、直接对象、纯字符串
      let tagsArr = []
      if (raw && typeof raw === 'object') {
        if (raw.tags !== undefined && typeof raw.tags === 'string') {
          tagsArr = raw.tags.split(',').filter(Boolean)
        } else if (raw.tags !== undefined && Array.isArray(raw.tags)) {
          tagsArr = raw.tags.filter(Boolean)
        } else {
          // 遍历对象 key，值为 true/1/非空的视为命中
          tagsArr = Object.keys(raw).filter(key => {
            if (key === 'tags' || key === 'deviceRisk' || key === 'deviceRiskDetail') return false
            const val = raw[key]
            return val === true || val === 1 || val === '1' || (typeof val === 'string' && val.trim() !== '')
          })
        }
      }

      // 阿里云风险标签释义来自后台 device_risk_tags
      const items = []
      tagsArr.forEach(key => {
        const cleanKey = key.trim()
        const info = this.riskTagDict[cleanKey]
        if (info) {
          items.push({
            key: cleanKey,
            label: info.label,
            isRisk: true,
            desc: info.desc
          })
        } else {
          items.push({
            key: cleanKey,
            label: cleanKey,
            isRisk: true,
            desc: '未知风险标签'
          })
        }
      })
      return items
    },
    // 格式化后的风险 JSON
    formattedRiskJson() {
      if (!this.detailUser.deviceRiskDetail) return ''
      try {
        const obj = typeof this.detailUser.deviceRiskDetail === 'string'
          ? JSON.parse(this.detailUser.deviceRiskDetail)
          : this.detailUser.deviceRiskDetail
        return JSON.stringify(obj, null, 2)
      } catch {
        return this.detailUser.deviceRiskDetail
      }
    }
  },
  mounted() {
    this.fetchUsers()
    this.fetchTotalEarned()
    this.fetchRiskTagDict()
  },
  methods: {
    async fetchRiskTagDict() {
      try {
        const result = await get('/admin/risk/aliyun-tags/dict')
        const list = result && Array.isArray(result.data) ? result.data : []
        const map = {}
        list.forEach((tag) => {
          if (!tag || !tag.tagState) return
          map[tag.tagState] = {
            label: tag.tagMean || tag.tagState,
            desc: tag.tagDesc || ''
          }
        })
        this.riskTagDict = map
      } catch (e) {
        console.error('获取阿里云风险标签字典失败:', e && e.message)
      }
    },
    async fetchTotalEarned() {
      try {
        const result = await get('/admin/users/total-earned')
        this.totalEarned = (result && result.data != null) ? result.data : 0
      } catch (e) {
        console.error('获取累计总收益失败:', e.message)
      }
    },

    async showLevel(user) {
      this.levelUser = {
        levelCode: user.levelCode,
        directCount: null,
        teamWithdrawAmount: null
      }
      this.levelDialogVisible = true
      this.levelLoading = true
      try {
        const result = await get(`/admin/leader-level/user-stats/${user.id}`)
        const data = (result && result.data) ? result.data : {}
        this.levelUser = {
          levelCode: data.levelCode != null ? data.levelCode : 0,
          directCount: data.directCount,
          teamWithdrawAmount: data.teamWithdrawAmount
        }
        // 同步列表行，避免下次再点仍看到旧缓存值
        user.levelCode = this.levelUser.levelCode
        user.directCount = this.levelUser.directCount
        user.teamWithdrawAmount = this.levelUser.teamWithdrawAmount
      } catch (e) {
        this.$message.error((e && e.message) || '获取团长等级失败')
      } finally {
        this.levelLoading = false
      }
    },
    formatUserLocation(user) {
      if (!user) return '-'
      if (user.location) return user.location
      const parts = [user.province, user.city].filter(Boolean)
      return parts.length ? parts.join('') : '-'
    },
    async showDetail(user) {
      this.detailUser = {
        id: user.id,
        nickname: user.nickname,
        avatarUrl: user.avatarUrl,
        lastIp: user.lastIp,
        province: user.province,
        city: user.city,
        location: user.location
      }
      this.showRawJson = false
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        this.detailUser = {
          id: data.id || user.id,
          nickname: data.nickname || user.nickname,
          avatarUrl: data.avatarUrl || user.avatarUrl,
          coinBalance: data.coinBalance,
          totalEarnedCoins: data.totalEarned,
          inviteRewardBalance: data.inviteRewardBalance,
          inviteRewardTotal: data.inviteRewardTotal,
          banDes: data.banDes || '',
          status: data.status !== undefined ? data.status : user.status,
          deviceRisk: data.deviceRisk !== undefined ? data.deviceRisk : user.deviceRisk,
          deviceRiskDetail: data.deviceRiskDetail || user.deviceRiskDetail || '',
          lastIp: user.lastIp || '',
          province: user.province || '',
          city: user.city || '',
          location: user.location || ''
        }
      } catch (e) {
        this.$message.error(e.message || '获取用户详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    async fetchUsers() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchUserId) {
          params.userId = this.searchUserId
        }
        if (this.searchPhone) {
          params.phone = this.searchPhone.trim()
        }
        if (this.searchStatus !== '' && this.searchStatus !== null) {
          params.status = this.searchStatus
        }

        const result = await get('/admin/users', params)
        const data = (result && result.data) ? result.data : {}
        this.users = Array.isArray(data.users) ? data.users : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取用户列表失败'
        this.users = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchUsers()
    },

    refreshList() {
      this.searchUserId = ''
      this.searchPhone = ''
      this.searchStatus = ''
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleMoreCommand(command, user) {
      if (command === 'level') {
        this.showLevel(user)
      } else if (command === 'toggle') {
        this.handleToggleStatus(user)
      } else if (command === 'delete') {
        this.handleDeleteUser(user)
      }
    },

    handleToggleStatus(user) {
      if (user.status === 0) {
        // 封禁用户：弹出对话框填写封禁描述
        this.banUser = user
        this.banForm.status = 1
        this.banForm.banDes = ''
        this.banDialogVisible = true
        this.$nextTick(() => {
          if (this.$refs.banFormRef) this.$refs.banFormRef.clearValidate()
        })
      } else {
        // 解封用户：直接发送
        this.doUpdateStatus(user, 0)
      }
    },
    handleDeleteUser(user) {
      this.$confirm(
        `确定删除用户「${user.nickname || user.openid}」吗？效果等同 App 注销，手机号可重新注册。`,
        '删除用户',
        { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
      )
        .then(async () => {
          try {
            await del(`/admin/users/${user.id}`)
            this.$message.success('已删除')
            this.fetchUsers()
          } catch (e) {
            this.$message.error((e && e.message) || '删除失败')
          }
        })
        .catch(() => {})
    },
    async doUpdateStatus(user, status, banDes = null) {
      const actionText = status === 1 ? '封禁' : '解封'
      try {
        const payload = { status }
        if (banDes) payload.banDes = banDes
        const res = await put(`/admin/users/${user.id}`, payload)
        this.$message.success(res.msg || `${actionText}成功`)
        user.status = status
        if (status === 0) user.banDes = null
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },
    async handleBanSubmit() {
      try {
        await this.$refs.banFormRef.validate()
      } catch {
        return
      }
      this.banDialogVisible = false
      await this.doUpdateStatus(this.banUser, this.banForm.status, this.banForm.banDes)
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 0;
}

.main-card {
  border-radius: 8px;
}

.filter-card {
  margin-bottom: 12px;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.filter-input-sm {
  width: 130px;
}

.filter-select-sm {
  width: 120px;
}

.earned-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%);
  border-radius: 8px;
  border: 1px solid #d1fae5;
  white-space: nowrap;
  flex-shrink: 0;
}

.earned-summary .el-icon-coin {
  font-size: 16px;
  color: #e6a23c;
}

.earned-label {
  font-size: 13px;
  color: #606266;
}

.earned-value {
  font-size: 18px;
  font-weight: 700;
  color: #059669;
}

.earned-unit {
  font-size: 12px;
  color: #909399;
}

.error-msg {
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

.user-ops {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  white-space: nowrap;
}

.user-ops .el-button {
  margin: 0;
  padding: 0 6px;
}

.data-table {
  border-radius: 6px;
  overflow: hidden;
}

.data-table::before {
  display: none;
}

.text-muted {
  color: #c0c4cc;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding: 4px 0;
}

.detail-content {
  padding: 4px 0;
}

.detail-header {
  text-align: center;
  padding: 8px 0 16px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}

.detail-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-top: 8px;
}

.detail-id {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.detail-location {
  font-size: 13px;
  color: #606266;
  margin-top: 6px;
}

.detail-ip {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.detail-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.detail-card {
  text-align: center;
  padding: 14px 10px;
  border-radius: 10px;
  border: 1px solid;
  min-width: 0;
}

.detail-card-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}

.detail-card-value {
  font-size: 22px;
  font-weight: 700;
  word-break: break-all;
  line-height: 1.25;
}

.detail-card-unit {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.coin-current {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.coin-current .detail-card-value {
  color: #16a34a;
}

.coin-total {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.coin-total .detail-card-value {
  color: #2563eb;
}

.coin-invite {
  background: #fff7ed;
  border-color: #fed7aa;
}

.coin-invite .detail-card-value {
  color: #ea580c;
}

.coin-invite-total {
  background: #fffbeb;
  border-color: #fde68a;
}

.coin-invite-total .detail-card-value {
  color: #d97706;
}

.detail-ban-reason {
  margin-top: 14px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ban-reason-label {
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 4px;
}

.ban-reason-text {
  font-size: 14px;
  color: #991b1b;
  line-height: 1.5;
}

.dialog-footer {
  text-align: center;
}

.detail-risk-section {
  margin-top: 14px;
  padding: 14px;
  background: #fafafa;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
}

.risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.risk-section-title .el-icon-warning-outline {
  font-size: 16px;
  color: #e6a23c;
  margin-right: 6px;
}

.risk-tag {
  margin-left: 8px;
}

.risk-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.risk-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  min-width: 180px;
  flex: 1;
}

.risk-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.risk-item-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}

.risk-item-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.risk-raw-json {
  margin-top: 10px;
  border-top: 1px dashed #dcdfe6;
  padding-top: 8px;
}

.risk-raw-title {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.risk-raw-title:hover {
  color: #409eff;
}

.risk-json-content {
  margin-top: 8px;
  padding: 12px;
  background: #2d2d2d;
  color: #f8f8f2;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.level-tip {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.main-card.animate__animated,
.data-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
